import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Modal } from '../components/ui';
import { ColorPicker } from '../components/ui/ColorPicker';
import { TagBadge } from '../components/ui/TagInput';
import { tagsAPI, type Tag as ApiTag } from '../services/api';
import { TAG_COLORS, type TagColorKey, getNextTagColor, tagNameToSlug } from '../utils/tagColors';

interface TagWithMeta extends ApiTag {
  // Extend API tag type if needed
}

// Adapt API tag to TagBadge format
function toTagBadgeFormat(tag: TagWithMeta) {
  return {
    id: tag.id,
    name: tag.name,
    slug: tag.slug,
    color: tag.color as TagColorKey,
  };
}

export default function TagManager() {
  const queryClient = useQueryClient();
  const [editingTag, setEditingTag] = useState<TagWithMeta | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState<TagColorKey>('blue');
  const [newTagAiDescription, setNewTagAiDescription] = useState('');
  const [newTagAiInstructions, setNewTagAiInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Fetch tags from API
  const { data, isLoading, error: fetchError } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsAPI.list(),
  });

  const tags: TagWithMeta[] = data?.data?.tags || [];

  // Create tag mutation
  const createMutation = useMutation({
    mutationFn: (tag: { name: string; color: string; ai_description?: string; ai_instructions?: string }) => tagsAPI.create(tag),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setNewTagName('');
      setNewTagColor(getNextTagColor(tags.map(t => t.color as TagColorKey)));
      setNewTagAiDescription('');
      setNewTagAiInstructions('');
      setIsCreateModalOpen(false);
      setError(null);
    },
    onError: (err: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      setError(err.response?.data?.error?.message || err.message || 'Failed to create tag');
    },
  });

  // Update tag mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Parameters<typeof tagsAPI.update>[1] }) =>
      tagsAPI.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setEditingTag(null);
      setError(null);
    },
    onError: (err: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      setError(err.response?.data?.error?.message || err.message || 'Failed to update tag');
    },
  });

  // Delete tag mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => tagsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setEditingTag(null);
    },
    onError: (err: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      setError(err.response?.data?.error?.message || err.message || 'Failed to delete tag');
    },
  });

  const handleCreateTag = () => {
    if (!newTagName.trim()) return;

    const slug = tagNameToSlug(newTagName);
    if (tags.some(t => t.slug === slug)) {
      setError('A tag with this name already exists');
      return;
    }

    createMutation.mutate({
      name: newTagName.trim(),
      color: newTagColor,
      ai_description: newTagAiDescription.trim() || undefined,
      ai_instructions: newTagAiInstructions.trim() || undefined,
    });
  };

  const handleUpdateTag = (updatedTag: TagWithMeta) => {
    updateMutation.mutate({
      id: updatedTag.id,
      updates: {
        name: updatedTag.name,
        color: updatedTag.color,
        ai_description: updatedTag.ai_description || null,
        ai_instructions: updatedTag.ai_instructions || null,
      },
    });
  };

  const handleDeleteTag = (tagId: string) => {
    if (confirm('Are you sure you want to delete this tag? It will be removed from all contacts.')) {
      deleteMutation.mutate(tagId);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
          <div className="bg-[var(--bg-card)] dark:bg-gray-800 rounded-xl p-6 space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <p className="text-red-700 dark:text-red-400">Failed to load tags. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tag Manager</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage your contact tags, colors, and <strong>AI Context</strong>
          </p>
        </div>
        <Button onClick={() => {
          setNewTagColor(getNextTagColor(tags.map(t => t.color as TagColorKey)));
          setIsCreateModalOpen(true);
          setError(null);
        }}>
          + New Tag
        </Button>
      </div>

      {/* Tags Grid */}
      <div className="bg-[var(--bg-card)] dark:bg-gray-800 rounded-xl border border-[var(--border-color)] dark:border-gray-700 overflow-hidden">
        <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 px-6 py-3 bg-[var(--bg-secondary)] dark:bg-gray-700/50 border-b border-[var(--border-color)] dark:border-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">
          <div>Tag</div>
          <div className="text-center">Color</div>
          <div className="text-center">Contacts</div>
          <div className="text-center">Actions</div>
        </div>

        {tags.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
            No tags yet. Create your first tag to get started.
          </div>
        ) : (
          <div className="divide-y divide-[var(--border-color)] dark:divide-gray-700">
            {tags.map((tag) => (
              <div
                key={tag.id}
                className="grid grid-cols-[1fr,auto,auto,auto] gap-4 px-6 py-4 items-center hover:bg-[var(--bg-secondary)] dark:hover:bg-gray-700/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <TagBadge tag={toTagBadgeFormat(tag)} />
                  {tag.ai_description && (
                    <span className="text-xs text-gray-400 dark:text-gray-500" title="Has AI context">
                      *
                    </span>
                  )}
                </div>

                <div className="flex justify-center">
                  <span className={`w-5 h-5 rounded-full ${TAG_COLORS[tag.color as TagColorKey]?.dot || 'bg-gray-400'}`} />
                </div>

                <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                  {tag.contact_count || 0}
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={() => {
                      setEditingTag(tag);
                      setError(null);
                    }}
                    className="px-3 py-1 text-sm text-ondeck-600 dark:text-ondeck-400 hover:bg-ondeck-50 dark:hover:bg-ondeck-900/30 rounded transition-colors"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Tag Modal */}
      <Modal
        open={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setError(null);
        }}
        title="Create New Tag"
        size="md"
      >
        <div className="px-6 pb-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Preview */}
          {newTagName.trim() && (
            <div className="flex items-center gap-3 p-3 bg-[var(--bg-secondary)] dark:bg-gray-700/50 rounded-lg">
              <span className="text-sm text-gray-500 dark:text-gray-400">Preview:</span>
              <TagBadge tag={{ id: 'preview', name: newTagName, slug: tagNameToSlug(newTagName), color: newTagColor }} />
            </div>
          )}

          {/* Name */}
          <Input
            label="Tag Name"
            placeholder="e.g., VIP Client, Open House Lead"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            autoFocus
          />

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Color
            </label>
            <ColorPicker value={newTagColor} onChange={setNewTagColor} />
          </div>

          {/* AI Context Section */}
          <div className="pt-4 border-t border-[var(--border-color)] dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <span>*</span> AI Context <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Description
                </label>
                <textarea
                  value={newTagAiDescription}
                  onChange={(e) => setNewTagAiDescription(e.target.value)}
                  placeholder="What does this tag mean? e.g., 'Sphere of Influence - people I know personally who may refer business'"
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-[var(--border-color)] dark:border-gray-600 rounded-lg bg-[var(--bg-card)] dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Special Instructions
                </label>
                <textarea
                  value={newTagAiInstructions}
                  onChange={(e) => setNewTagAiInstructions(e.target.value)}
                  placeholder="How should AI handle contacts with this tag? e.g., 'Prioritize for regular check-ins, suggest personal touches like birthday messages'"
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-[var(--border-color)] dark:border-gray-600 rounded-lg bg-[var(--bg-card)] dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-[var(--border-color)] dark:border-gray-700">
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateModalOpen(false);
                setError(null);
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTag}
              className="flex-1"
              disabled={!newTagName.trim()}
              loading={createMutation.isPending}
            >
              Create Tag
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Tag Modal */}
      {editingTag && (
        <EditTagModal
          tag={editingTag}
          onSave={handleUpdateTag}
          onDelete={() => handleDeleteTag(editingTag.id)}
          onClose={() => {
            setEditingTag(null);
            setError(null);
          }}
          isLoading={updateMutation.isPending}
          error={error}
        />
      )}
    </div>
  );
}

// Edit Tag Modal Component
function EditTagModal({
  tag,
  onSave,
  onDelete,
  onClose,
  isLoading,
  error,
}: {
  tag: TagWithMeta;
  onSave: (tag: TagWithMeta) => void;
  onDelete: () => void;
  onClose: () => void;
  isLoading?: boolean;
  error?: string | null;
}) {
  const [name, setName] = useState(tag.name);
  const [color, setColor] = useState<TagColorKey>(tag.color as TagColorKey);
  const [aiDescription, setAiDescription] = useState(tag.ai_description || '');
  const [aiInstructions, setAiInstructions] = useState(tag.ai_instructions || '');

  const handleSave = () => {
    onSave({
      ...tag,
      name,
      slug: tagNameToSlug(name),
      color,
      ai_description: aiDescription || undefined,
      ai_instructions: aiInstructions || undefined,
    });
  };

  return (
    <Modal open={true} onClose={onClose} title="Edit Tag" size="md">
      <div className="px-6 pb-6 space-y-4">
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Preview */}
        <div className="flex items-center gap-3 p-3 bg-[var(--bg-secondary)] dark:bg-gray-700/50 rounded-lg">
          <span className="text-sm text-gray-500 dark:text-gray-400">Preview:</span>
          <TagBadge tag={{ id: tag.id, name, slug: tagNameToSlug(name), color }} />
        </div>

        {/* Name */}
        <Input
          label="Tag Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        {/* Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            Color
          </label>
          <ColorPicker value={color} onChange={setColor} />
        </div>

        {/* AI Context Section */}
        <div className="pt-4 border-t border-[var(--border-color)] dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <span>*</span> AI Context
          </h3>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Description
              </label>
              <textarea
                value={aiDescription}
                onChange={(e) => setAiDescription(e.target.value)}
                placeholder="What does this tag mean? e.g., 'Sphere of Influence - people I know personally who may refer business'"
                rows={2}
                className="w-full px-3 py-2 text-sm border border-[var(--border-color)] dark:border-gray-600 rounded-lg bg-[var(--bg-card)] dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Special Instructions
              </label>
              <textarea
                value={aiInstructions}
                onChange={(e) => setAiInstructions(e.target.value)}
                placeholder="How should AI handle contacts with this tag? e.g., 'Prioritize for regular check-ins, suggest personal touches like birthday messages'"
                rows={2}
                className="w-full px-3 py-2 text-sm border border-[var(--border-color)] dark:border-gray-600 rounded-lg bg-[var(--bg-card)] dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-[var(--border-color)] dark:border-gray-700">
          <button
            onClick={onDelete}
            className="text-sm text-red-600 dark:text-red-400 hover:underline"
          >
            Delete Tag
          </button>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!name.trim()} loading={isLoading}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
